import InspectLink from '../../components/InspectLink';

import { Absent, Field, FieldList } from './AccountFields';
import { NameOrId } from './AccountLinks';
import { formatCaregiverRelationship, formatPartnerRelationship, formatPersonName } from './accountsView';

import type { AdminGetFamilyAccountQuery } from './__generated__/AccountsScene.gql';

type Dependent = NonNullable<AdminGetFamilyAccountQuery['adminGetFamilyAccount']>['dependents'][number];

interface IDependentsPanelProps {
  dependents: Dependent[];
}

/**
 * The household's loved-one profiles, oldest first.
 *
 * Lists enrolled profiles as well as unenrolled ones: an enrolled loved one's
 * contact and sign-in state belong to the Users panel above, but their date of
 * birth, relationships, and partnerships exist only on the profile and would
 * otherwise be unreadable.
 */
const DependentsPanel = ({ dependents }: IDependentsPanelProps) => (
  <section className="flex min-w-0 flex-col gap-3">
    <h2 className="border-border-primary text-fg-primary flex items-baseline gap-2 border-b pb-2 text-sm font-semibold uppercase tracking-widest">
      Loved ones
      <span className="text-fg-tertiary text-xs font-normal normal-case tracking-normal">{dependents.length}</span>
    </h2>

    {dependents.length === 0 ? (
      <div className="text-fg-tertiary text-xs italic">No loved-one profiles in this household.</div>
    ) : (
      <div className="flex flex-col gap-4">
        {dependents.map((dependent) => (
          <DependentBlock key={dependent.id} dependent={dependent} />
        ))}
      </div>
    )}
  </section>
);

interface IDependentBlockProps {
  dependent: Dependent;
}

const DependentBlock = ({ dependent }: IDependentBlockProps) => {
  const isEnrolled = (dependent.linkedUserId ?? null) !== null;

  return (
    <div className="flex min-w-0 flex-col gap-1">
      <div className="flex items-baseline gap-2">
        <span className="text-fg-primary text-sm font-semibold">
          {formatPersonName(dependent, dependent.displayName)}
        </span>
        {isEnrolled && (
          <span
            className="border-border-primary text-fg-tertiary whitespace-nowrap rounded-sm border px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide"
            title="This loved one holds an account; their contact and sign-in state are on the Users tab"
          >
            has account
          </span>
        )}
      </div>

      <FieldList>
        <Field label="Profile id" mono>
          <InspectLink id={dependent.id} />
        </Field>
        <Field label="Date of birth">{dependent.dateOfBirth ?? <Absent>Not recorded</Absent>}</Field>
        <Field label="Relationship">
          {dependent.relationships.length === 0 ? (
            <Absent>None recorded</Absent>
          ) : (
            <div className="flex flex-col gap-0.5">
              {dependent.relationships.map((relationship) => (
                <span key={relationship.caregiverUserId}>
                  {formatCaregiverRelationship(relationship)}{' '}
                  <span className="text-fg-tertiary">
                    of <NameOrId name={relationship.caregiverName} />
                  </span>
                  {relationship.livesWithCaregiver === true && (
                    <span className="text-fg-tertiary text-xs"> · lives together</span>
                  )}
                </span>
              ))}
            </div>
          )}
        </Field>
        {dependent.partnerships.length > 0 && (
          <Field label="Partner">
            <div className="flex flex-col gap-0.5">
              {dependent.partnerships.map((partnership) => (
                <span key={partnership.partnerDependentId}>
                  <NameOrId name={partnership.partnerName} />{' '}
                  <span className="text-fg-tertiary">({formatPartnerRelationship(partnership)})</span>
                  {partnership.liveTogether === false && (
                    <span className="text-fg-tertiary text-xs"> · lives apart</span>
                  )}
                </span>
              ))}
            </div>
          </Field>
        )}
        {/* The database clears a profile's own contact and timezone once an
            account owns them, so these rows would always read empty for an
            enrolled loved one. */}
        {!isEnrolled && (
          <>
            <Field label="Email" mono>
              {dependent.email ?? <Absent>None</Absent>}
            </Field>
            <Field label="Phone" mono>
              {dependent.phoneNumber ?? <Absent>None</Absent>}
            </Field>
          </>
        )}
        <Field label="Address">{dependent.address ?? <Absent>None</Absent>}</Field>
        {!isEnrolled && <Field label="Timezone">{dependent.timezone ?? <Absent>Not set</Absent>}</Field>}
      </FieldList>
    </div>
  );
};

export default DependentsPanel;
